import React, { useEffect, useState, useRef, useContext } from "react";
import "./css/CharacterChatTextBox.css";
import { ReactComponent as CloseIcon } from "pixelarticons/svg/close.svg";
import { ReactComponent as ZapIcon } from "pixelarticons/svg/volume-2.svg";
import { ReactComponent as TranslateIcon } from "pixelarticons/svg/ab-testing.svg";
import CurrentMissionContext from "./CurrentMissionContext";
import { speakWithWhisper } from "./whisper";

export const CharacterChatTextBox = () => {
  const [isChatBoxOpen, setIsChatBoxOpen] = useState(false);
  const [character, setCharacter] = useState({});
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState("");

  const [isListening, setIsListening] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const recognition = useRef(null);

  const currentMission = useContext(CurrentMissionContext);

  const handlePlayWhisper = (text) => {
    speakWithWhisper(text);
  };

  useEffect(() => {
    window.ReactInterface.showCharacterChat = (character) => {
      setCharacter(character);
      setIsChatBoxOpen(true);
    };

    const handleKeyDown = (e) => {
      if (
        ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key) &&
        isChatBoxOpen
      ) {
        setIsChatBoxOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isChatBoxOpen]);

  useEffect(() => {
    window.ReactInterface.showCharacterChat = (character) => {
      setCharacter(character);
      setIsChatBoxOpen(true);
    };
  }, []);

  useEffect(() => {
    setChatHistory([]);
  }, [character]);

  useEffect(() => {
    if ("webkitSpeechRecognition" in window) {
      recognition.current = new window.webkitSpeechRecognition();
      recognition.current.lang = "en-US";
      recognition.current.continuous = true;
      recognition.current.interimResults = true;

      recognition.current.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; ++i) {
          if (e.results[i].isFinal) {
            setFinalTranscript(
              (finalTranscript) => finalTranscript + e.results[i][0].transcript
            );
          } else {
            interim += e.results[i][0].transcript;
          }
        }
        setInterimTranscript(interim);
      };

      recognition.current.onend = () => {
        if (isListening) {
          recognition.current.start();
        }
      };
    } else {
      console.error("Speech recognition is not supported in this browser.");
    }
  }, [isListening]);

  useEffect(() => {
    setChatInput(finalTranscript + interimTranscript);
  }, [finalTranscript, interimTranscript]);

  const handleSendMessage = async () => {
    if (chatInput.trim()) {
      const newMessage = { text: chatInput, sender: "player" };

      setChatHistory([...chatHistory, newMessage]);

      let requestBody;

      try {
        requestBody = JSON.stringify({
          content: chatInput,
          name: character.Name,
          history: chatHistory.map((message) => message.text).join("\n"),
          mission:
            "To complete all assignments for How to Make (almost) Anything",
          role: "a brilliant fab lab instructor helping out for MIT Media Lab's class, How to Make (almost) Anything",
        });
      } catch {
        requestBody = JSON.stringify({
          content: "Hello!",
          name: "Instructor",
          history: "",
          mission:
            "To complete all assignments for How to Make (almost) Anything",
          role: "a brilliant fab lab instructor helping out for MIT Media Lab's class, How to Make (almost) Anything",
        });
      }

      const response = await fetch("https://htmaaland-3c97b27faa21.herokuapp.com/chat", {
        method: "POST",
        body: requestBody,
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        const data = await response.json();
        console.log(data);

        if (data.Action === "Speak") {
          setChatHistory([
            ...chatHistory,
            newMessage,
            { text: data.ActionInput.text, sender: "server" },
          ]);
        }
      } else {
        console.error(
          "Error sending chat message:",
          response.status,
          response.statusText
        );
      }

      setChatInput("");
      setFinalTranscript("");
      setInterimTranscript("");
    }
  };

  useEffect(() => {
    if (finalTranscript || interimTranscript) {
      setChatInput(finalTranscript + interimTranscript);
    }
  }, [finalTranscript, interimTranscript]);

  const toggleListening = () => {
    setIsListening(!isListening);
    if (!isListening) {
      recognition.current.start();
    } else {
      recognition.current.stop();
    }
  };

  const handleKeyDown = (event) => {
    event.stopPropagation();
  };

  const handleKeyUp = (event) => {
    event.stopPropagation();
  };

  const closeChatBox = () => {
    setIsChatBoxOpen(false);
  };

  // const handleSendMessage = () => {
  //   if (chatInput.trim()) {
  //     setChatHistory([...chatHistory, { text: chatInput, sender: "player" }]);
  //     setChatInput("");
  //     // TODO handle send
  //   }
  // };

  const handleInputChange = (event) => {
    setChatInput(event.target.value);
  };

  return (
    <div>
      {isChatBoxOpen && (
        <div className="helper-tools">
          <div className="interactive-text-box">
            <div className="task-info">
              <h3>{character.Name}</h3>
              <div className="task-info small">{character.Backstory}</div>
            </div>
            <div className="chat-input">
              <img
                src={character.ProfileAssetURL}
                alt={character.Name}
                className="chat-image"
              />
              <textarea
                value={chatInput}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
              ></textarea>
              <div className="buttons">
                <button onClick={handleSendMessage}>Send</button>
                <button onClick={toggleListening}>
                  <img
                    src="./assets/microphone.svg"
                    alt="Microphone"
                    width="30"
                    height="30"
                  />
                  {isListening && "..."}
                </button>
              </div>
            </div>
            <div className="chat-history">
              {chatHistory.map((message, index) => (
                <p
                  className={
                    message.sender === "player"
                      ? "player-message"
                      : "server-message"
                  }
                >
                  {message.text}
                  <div className="characterchat-icons">
                    <ZapIcon
                      onClick={() => handlePlayWhisper(message.text)}
                      className="icon zap-icon-small"
                    />
                  </div>
                </p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
